// MatchResultModal.tsx
// Created by Taulant Geci

import React, { useState } from 'react';
import { Match, Player } from '../models/types';
import { MatchGeneratorController } from '../modules/MatchGeneratorController';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faTrophy, faUsers, faUser } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';

interface MatchResultModalProps {
  match: Match;
  controller: MatchGeneratorController;
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
}

export const MatchResultModal: React.FC<MatchResultModalProps> = ({
  match,
  controller,
  isOpen,
  onClose,
  onSaved
}) => {
  const { t } = useTranslation();
  const [selectedWinners, setSelectedWinners] = useState<Set<string>>(new Set());
  const [showScoreEntry, setShowScoreEntry] = useState(false);
  const [team1Score, setTeam1Score] = useState('');
  const [team2Score, setTeam2Score] = useState('');

  if (!isOpen) return null;

  const handlePlayerToggle = (playerId: string) => {
    const newSelected = new Set(selectedWinners);
    if (newSelected.has(playerId)) {
      newSelected.delete(playerId);
    } else {
      newSelected.add(playerId);
    }
    setSelectedWinners(newSelected);
  };

  const handleSaveResult = () => {
    if (selectedWinners.size === 0) return;

    const winnerIds = Array.from(selectedWinners);
    const loserIds = match.players
      .filter(player => !selectedWinners.has(player.id))
      .map(player => player.id);

    controller.recordMatchResult(match.id, winnerIds, loserIds);
    
    onSaved();
    onClose();
    resetModal();
  };

  const handleSaveWithScore = () => {
    if (!team1Score || !team2Score) return;

    const score1 = parseInt(team1Score);
    const score2 = parseInt(team2Score);
    
    if (isNaN(score1) || isNaN(score2) || score1 === score2) return;

    const team1Players = match.isDoubles ? 
      match.players.slice(0, 2) : [match.players[0]];
    const team2Players = match.isDoubles ? 
      match.players.slice(2, 4) : [match.players[1]];

    const isTeam1Winner = score1 > score2;
    const winnerIds = isTeam1Winner ? 
      team1Players.map(p => p.id) : team2Players.map(p => p.id);
    const loserIds = isTeam1Winner ? 
      team2Players.map(p => p.id) : team1Players.map(p => p.id);
    const winnerScore = Math.max(score1, score2);
    const loserScore = Math.min(score1, score2);

    controller.recordMatchResult(match.id, winnerIds, loserIds, winnerScore, loserScore);
    
    onSaved();
    onClose();
    resetModal();
  };

  const resetModal = () => {
    setSelectedWinners(new Set());
    setShowScoreEntry(false);
    setTeam1Score('');
    setTeam2Score('');
  };

  const canSave = selectedWinners.size > 0 && 
    (match.isDoubles ? selectedWinners.size === 2 : selectedWinners.size === 1);

  const team1Players = match.isDoubles ? match.players.slice(0, 2) : [match.players[0]];
  const team2Players = match.isDoubles ? match.players.slice(2, 4) : [match.players[1]];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content match-result-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>
            <FontAwesomeIcon icon={faTrophy} className="me-2" />
            {t('recordResult')}
          </h3>
          <button className="modal-close-btn" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="modal-body">
          {!showScoreEntry ? (
            <>
              {/* Match Info */}
              <div className="match-info-section">
                <div className="match-court-info">
                  <FontAwesomeIcon icon={match.isDoubles ? faUsers : faUser} className="me-2" />
                  {t('court_number', { number: match.court.number })} - {match.isDoubles ? t('doubles') : t('singles')}
                </div>
              </div>

              {/* Winner Selection */}
              <div className="winner-selection-section">
                <h4>{t('selectWinners')}:</h4>
                
                {match.isDoubles ? (
                  <div className="teams-selection">
                    <div className="team-option">
                      <h5>{t('team1')}</h5>
                      {team1Players.map(player => (
                        <div 
                          key={player.id} 
                          className={`player-selection-row ${selectedWinners.has(player.id) ? 'selected' : ''}`}
                          onClick={() => handlePlayerToggle(player.id)}
                        >
                          <div className="player-info">
                            <FontAwesomeIcon icon={faUser} className="me-2" />
                            {player.name}
                          </div>
                          {selectedWinners.has(player.id) && (
                            <FontAwesomeIcon icon={faTrophy} className="winner-icon" />
                          )}
                        </div>
                      ))}
                    </div>

                    <div className="team-option">
                      <h5>{t('team2')}</h5>
                      {team2Players.map(player => (
                        <div 
                          key={player.id} 
                          className={`player-selection-row ${selectedWinners.has(player.id) ? 'selected' : ''}`}
                          onClick={() => handlePlayerToggle(player.id)}
                        >
                          <div className="player-info">
                            <FontAwesomeIcon icon={faUser} className="me-2" />
                            {player.name}
                          </div>
                          {selectedWinners.has(player.id) && (
                            <FontAwesomeIcon icon={faTrophy} className="winner-icon" />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="players-selection">
                    {match.players.map(player => (
                      <div 
                        key={player.id} 
                        className={`player-selection-row ${selectedWinners.has(player.id) ? 'selected' : ''}`}
                        onClick={() => handlePlayerToggle(player.id)}
                      >
                        <div className="player-info">
                          <FontAwesomeIcon icon={faUser} className="me-2" />
                          {player.name}
                        </div>
                        {selectedWinners.has(player.id) && (
                          <FontAwesomeIcon icon={faTrophy} className="winner-icon" />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="modal-actions">
                <button 
                  className="btn btn-outline-primary"
                  onClick={() => setShowScoreEntry(true)}
                  disabled={!canSave}
                >
                  {t('enterScore')}
                </button>
                <button 
                  className="btn btn-primary"
                  onClick={handleSaveResult}
                  disabled={!canSave}
                >
                  {t('save')}
                </button>
              </div>
            </>
          ) : (
            <>
              {/* Score Entry */}
              <div className="score-entry-section">
                <h4>{t('enterScore')}:</h4>
                
                <div className="score-input-container">
                  <div className="team-score">
                    <div className="team-names">
                      {team1Players.map(player => (
                        <div key={player.id} className="player-name">{player.name}</div>
                      ))}
                    </div>
                    <input
                      type="number"
                      value={team1Score}
                      onChange={(e) => setTeam1Score(e.target.value)}
                      className="score-input"
                      placeholder="0"
                      min="0"
                    />
                  </div>

                  <div className="score-separator">:</div>

                  <div className="team-score">
                    <div className="team-names">
                      {team2Players.map(player => (
                        <div key={player.id} className="player-name">{player.name}</div>
                      ))}
                    </div>
                    <input
                      type="number"
                      value={team2Score}
                      onChange={(e) => setTeam2Score(e.target.value)}
                      className="score-input"
                      placeholder="0"
                      min="0"
                    />
                  </div>
                </div>

                {team1Score && team2Score && parseInt(team1Score) === parseInt(team2Score) && (
                  <div className="error-message">
                    {t('cannotBeTie')}
                  </div>
                )}
              </div>

              <div className="modal-actions">
                <button 
                  className="btn btn-outline-secondary"
                  onClick={() => setShowScoreEntry(false)}
                >
                  {t('back')}
                </button>
                <button 
                  className="btn btn-primary"
                  onClick={handleSaveWithScore}
                  disabled={!team1Score || !team2Score || parseInt(team1Score) === parseInt(team2Score)}
                >
                  {t('save')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}; 